import * as React from "react";
import type { MessageDescriptor } from "@lingui/core";
import { msg } from "@lingui/core/macro";
import { useLingui } from "@lingui/react/macro";
import { Info, Loader2, RotateCcw, Save } from "lucide-react";

import { Button, cn, useToast } from "@ando/ui";

import {
  useAdminOrgBranding,
  useResetAdminOrgBranding,
  useUpdateAdminOrgBranding,
} from "@/features/admin/hooks/useAdminOrg";

import { BeGapBanner, ConfirmDialog, LoadingRow } from "./_shared";
import { OrgBrandingPreview } from "./OrgBrandingPreview";

/**
 * Branding tab — admin view + edit of org branding with live preview.
 *
 * v1 reference: ``OrgBrandingTab.tsx`` (237 LoC) +
 * ``OrgBrandingPreview.tsx`` (146 LoC). The v1 surface uses a
 * dedicated ``BrandLogoInput`` widget for uploads. v2 ships a focused
 * URL-only editor + colour pickers; logo upload via a dropzone is on
 * the BE-gap list (no dedicated upload endpoint outside platform-admin
 * scoped POST that lives in v1). TODO Stream Branding: port
 * ``brand_logo_url`` upload widget once the upload endpoint is reused.
 *
 * v2 mounts ``<OrgBrandingPreview>`` below the color pickers so admins
 * can iterate on the login page look without saving.
 */

export interface OrgBrandingTabProps {
  orgId: string;
  orgName: string;
}

const HEX_RE = /^#?(?:[0-9a-fA-F]{3}|[0-9a-fA-F]{6})$/;

function validateHex(input: string): MessageDescriptor | null {
  if (!input) return null;
  if (!HEX_RE.test(input)) return msg`Inserisci un colore esadecimale valido.`;
  return null;
}

function validateHttpsUrl(input: string): MessageDescriptor | null {
  if (!input) return null;
  try {
    const u = new URL(input);
    if (u.protocol !== "https:") return msg`L'URL deve usare HTTPS.`;
  } catch {
    return msg`URL non valido.`;
  }
  return null;
}

function Field({
  label,
  htmlFor,
  error,
  children,
}: {
  label: string;
  htmlFor: string;
  error?: string | null;
  children: React.ReactNode;
}): React.ReactElement {
  return (
    <div className="space-y-1.5">
      <label
        htmlFor={htmlFor}
        className="font-semibold text-[10px] text-ink-3 uppercase tracking-wide"
      >
        {label}
      </label>
      {children}
      {error ? (
        <span className="block text-[11px] text-destructive">{error}</span>
      ) : null}
    </div>
  );
}

export function OrgBrandingTab({
  orgId,
  orgName,
}: OrgBrandingTabProps): React.ReactElement {
  const { i18n } = useLingui();
  const { toast } = useToast();
  const brandingQuery = useAdminOrgBranding(orgId);
  const updateBranding = useUpdateAdminOrgBranding(orgId);
  const resetBranding = useResetAdminOrgBranding(orgId);

  const [brandName, setBrandName] = React.useState("");
  const [primary, setPrimary] = React.useState("");
  const [accent, setAccent] = React.useState("");
  const [logoUrl, setLogoUrl] = React.useState("");
  const [logoDarkUrl, setLogoDarkUrl] = React.useState("");
  const [faviconUrl, setFaviconUrl] = React.useState("");
  const [confirmReset, setConfirmReset] = React.useState(false);

  React.useEffect(() => {
    const b = brandingQuery.data;
    if (!b) return;
    setBrandName(b.brand_name ?? "");
    setPrimary(b.brand_primary_color ?? "");
    setAccent(b.brand_accent_color ?? "");
    setLogoUrl(b.brand_logo_url ?? "");
    setLogoDarkUrl(b.brand_logo_dark_url ?? "");
    setFaviconUrl(b.brand_favicon_url ?? "");
  }, [brandingQuery.data]);

  const errors = {
    primary: validateHex(primary),
    accent: validateHex(accent),
    logo: validateHttpsUrl(logoUrl),
    logoDark: validateHttpsUrl(logoDarkUrl),
    favicon: validateHttpsUrl(faviconUrl),
  };
  const hasErrors = Object.values(errors).some(Boolean);

  const handleSave = (): void => {
    updateBranding.mutate(
      {
        brand_name: brandName || null,
        brand_primary_color: primary || null,
        brand_accent_color: accent || null,
        brand_logo_url: logoUrl || null,
        brand_logo_dark_url: logoDarkUrl || null,
        brand_favicon_url: faviconUrl || null,
      },
      {
        onSuccess: () =>
          toast.success(i18n._(msg`Branding salvato`), {
            description: i18n._(msg`Modifiche applicate.`),
          }),
        onError: (err) =>
          toast.error(i18n._(msg`Branding`), {
            description:
              err instanceof Error ? err.message : i18n._(msg`Errore inatteso`),
          }),
      },
    );
  };

  const handleResetConfirmed = (): void => {
    resetBranding.mutate(undefined, {
      onSuccess: () => {
        toast.success(i18n._(msg`Branding resettato`), {
          description: i18n._(msg`Default ripristinati.`),
        });
        setConfirmReset(false);
      },
      onError: (err) =>
        toast.error(i18n._(msg`Reset fallito`), {
          description:
            err instanceof Error ? err.message : i18n._(msg`Errore inatteso`),
        }),
    });
  };

  if (brandingQuery.isLoading) {
    return <LoadingRow label={i18n._(msg`Caricamento branding…`)} />;
  }

  return (
    <div className="flex flex-col gap-4">
      <div
        role="note"
        className="flex items-start gap-3 rounded-lg border border-hairline bg-transparent px-4 py-3 text-[12.5px] text-ink-2"
      >
        <Info
          size={14}
          aria-hidden
          className="mt-0.5 shrink-0 text-warning-foreground"
        />
        <div className="space-y-1">
          <p className="font-medium text-ink-1">
            {i18n._(msg`Personalizzazione pagina di login per i membri di ${orgName}.`)}
          </p>
          <p className="text-[12px] text-ink-3">
            {i18n._(
              msg`Logo, colori e favicon appaiono solo agli utenti di questa organizzazione.`,
            )}
          </p>
        </div>
      </div>

      <div className="flex flex-wrap justify-end gap-2">
        <Button
          type="button"
          variant="outline"
          size="sm"
          onClick={() => setConfirmReset(true)}
          disabled={resetBranding.isPending}
        >
          <RotateCcw size={13} aria-hidden className="mr-1.5" />
          {i18n._(msg`Reset`)}
        </Button>
        <Button
          type="button"
          size="sm"
          onClick={handleSave}
          disabled={hasErrors || updateBranding.isPending}
        >
          {updateBranding.isPending ? (
            <Loader2 size={13} aria-hidden className="mr-1.5 animate-spin" />
          ) : (
            <Save size={13} aria-hidden className="mr-1.5" />
          )}
          {i18n._(msg`Salva`)}
        </Button>
      </div>

      <section
        aria-labelledby="org-branding-form"
        className="rounded-lg border border-hairline bg-card/55 p-5"
      >
        <h2
          id="org-branding-form"
          className="mb-4 font-semibold text-[10px] text-ink-3 uppercase tracking-wide"
        >
          {i18n._(msg`Identità di brand`)}
        </h2>
        <div className="grid gap-4 md:grid-cols-2">
          <Field label={i18n._(msg`Nome brand`)} htmlFor="brand-name">
            <input
              id="brand-name"
              type="text"
              value={brandName}
              onChange={(e) => setBrandName(e.target.value)}
              placeholder={orgName}
              className="h-8 w-full rounded-md border border-hairline bg-card px-2.5 text-[12.5px] text-ink-1 focus-visible:border-accent focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent/40"
            />
          </Field>
          <div className="md:col-span-1" />

          <Field
            label={i18n._(msg`Colore primario`)}
            htmlFor="brand-primary"
            error={errors.primary ? i18n._(errors.primary) : null}
          >
            <div className="flex items-center gap-2">
              <input
                aria-label={i18n._(msg`Selettore colore primario`)}
                type="color"
                value={primary.startsWith("#") ? primary : `#${primary || "ff6f3c"}`}
                onChange={(e) => setPrimary(e.target.value)}
                className="h-8 w-10 cursor-pointer rounded border border-hairline"
              />
              <input
                id="brand-primary"
                type="text"
                value={primary}
                onChange={(e) => setPrimary(e.target.value)}
                placeholder="#ff6f3c"
                className={cn(
                  "h-8 w-36 rounded-md border bg-card px-2.5 font-mono text-[12.5px] text-ink-1 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent/40",
                  errors.primary
                    ? "border-destructive"
                    : "border-hairline focus-visible:border-accent",
                )}
              />
            </div>
          </Field>
          <Field
            label={i18n._(msg`Colore accent`)}
            htmlFor="brand-accent"
            error={errors.accent ? i18n._(errors.accent) : null}
          >
            <div className="flex items-center gap-2">
              <input
                aria-label={i18n._(msg`Selettore colore accent`)}
                type="color"
                value={accent.startsWith("#") ? accent : `#${accent || "fff4ee"}`}
                onChange={(e) => setAccent(e.target.value)}
                className="h-8 w-10 cursor-pointer rounded border border-hairline"
              />
              <input
                id="brand-accent"
                type="text"
                value={accent}
                onChange={(e) => setAccent(e.target.value)}
                placeholder="#fff4ee"
                className={cn(
                  "h-8 w-36 rounded-md border bg-card px-2.5 font-mono text-[12.5px] text-ink-1 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent/40",
                  errors.accent
                    ? "border-destructive"
                    : "border-hairline focus-visible:border-accent",
                )}
              />
            </div>
          </Field>

          <Field
            label={i18n._(msg`Logo (light)`)}
            htmlFor="brand-logo"
            error={errors.logo ? i18n._(errors.logo) : null}
          >
            <input
              id="brand-logo"
              type="url"
              value={logoUrl}
              onChange={(e) => setLogoUrl(e.target.value)}
              placeholder="https://…"
              className="h-8 w-full rounded-md border border-hairline bg-card px-2.5 text-[12.5px] text-ink-1 focus-visible:border-accent focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent/40"
            />
          </Field>
          <Field
            label={i18n._(msg`Logo (dark)`)}
            htmlFor="brand-logo-dark"
            error={errors.logoDark ? i18n._(errors.logoDark) : null}
          >
            <input
              id="brand-logo-dark"
              type="url"
              value={logoDarkUrl}
              onChange={(e) => setLogoDarkUrl(e.target.value)}
              placeholder="https://…"
              className="h-8 w-full rounded-md border border-hairline bg-card px-2.5 text-[12.5px] text-ink-1 focus-visible:border-accent focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent/40"
            />
          </Field>

          <Field
            label={i18n._(msg`Favicon`)}
            htmlFor="brand-favicon"
            error={errors.favicon ? i18n._(errors.favicon) : null}
          >
            <input
              id="brand-favicon"
              type="url"
              value={faviconUrl}
              onChange={(e) => setFaviconUrl(e.target.value)}
              placeholder="https://…"
              className="h-8 w-full rounded-md border border-hairline bg-card px-2.5 text-[12.5px] text-ink-1 focus-visible:border-accent focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent/40"
            />
          </Field>
        </div>
      </section>

      <OrgBrandingPreview
        brandName={brandName}
        brandPrimaryColor={primary}
        brandAccentColor={accent}
        brandLogoUrl={logoUrl}
        fallbackName={orgName}
      />

      <BeGapBanner
        message={
          <>
            {i18n._(
              msg`Upload diretto del logo non ancora portato — solo URL HTTPS. TODO Stream Branding: portare il dropzone di v1`,
            )}{" "}
            <code className="font-mono">BrandLogoInput</code>.
          </>
        }
      />

      <ConfirmDialog
        open={confirmReset}
        onOpenChange={setConfirmReset}
        title={i18n._(msg`Reset branding?`)}
        description={i18n._(
          msg`Ripristina i colori e i loghi ai default di ANDO. Questa operazione cancella i valori salvati.`,
        )}
        confirmLabel={
          resetBranding.isPending ? i18n._(msg`Reset…`) : i18n._(msg`Reset`)
        }
        destructive
        loading={resetBranding.isPending}
        onConfirm={handleResetConfirmed}
      />
    </div>
  );
}
